// src/features/goal/constants/categoryStyles.ts
// Category 樣式系統 - 統一配色和視覺效果
//
// 設計理念：
// - Ceramic Gallery 風格：陶瓷般的質感和配色
// - 統一管理所有 Category 相關的視覺樣式
// - 支援 Objective 9 個分類的配色系統
// - 使用 Tailwind 語義化類名

import { ObjectiveCategory } from '../types/objective';

/**
 * Category 樣式變體型別
 * 用於 Ceramic Gallery 的視覺設計系統
 */
export interface CategoryVariant {
  color: string;       // 文字和圖示顏色 (text-* class)
  shadow: string;      // 圖示陰影效果 (drop-shadow-* class)
  bgClass: string;     // 圓形容器背景漸層 (bg-gradient-* classes)
  borderClass: string; // 圓形容器邊框顏色 (border-* class)
  cardHoverBg: string; // 整體卡片懸停背景 (hover:bg-* class)
}

/**
 * CERAMIC_VARIANTS - Ceramic Gallery 配色系統
 *
 * 為每個目標類別定義統一的視覺風格：
 * - work: 藍色系（專業、穩重）
 * - health: 玫瑰色系（活力、健康）
 * - growth: 紫色系（成長、智慧）
 * - finance: 綠寶石色系（財富、穩定）
 * - romance: 粉色系（浪漫、溫暖）
 * - social: 橙色系（社交、熱情）
 * - leisure: 青色系（休閒、放鬆）
 * - environment: 琥珀色系（自然、環保）
 * - family: 靛藍色系（家庭、溫馨）
 */
export const CERAMIC_VARIANTS: Record<ObjectiveCategory, CategoryVariant> = {
  work: {
    color: 'text-sky-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(37,99,235,0.3)]',
    bgClass: 'bg-gradient-to-br from-sky-50 to-sky-100',
    borderClass: 'border-sky-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  health: {
    color: 'text-rose-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(225,29,72,0.3)]',
    bgClass: 'bg-gradient-to-br from-rose-50 to-rose-100',
    borderClass: 'border-rose-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  growth: {
    color: 'text-sky-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(147,51,234,0.3)]',
    bgClass: 'bg-gradient-to-br from-sky-50 to-sky-100',
    borderClass: 'border-sky-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  finance: {
    color: 'text-emerald-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(5,150,105,0.3)]',
    bgClass: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
    borderClass: 'border-emerald-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  romance: {
    color: 'text-pink-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(219,39,119,0.3)]',
    bgClass: 'bg-gradient-to-br from-pink-50 to-pink-100',
    borderClass: 'border-pink-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  social: {
    color: 'text-orange-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(234,88,12,0.3)]',
    bgClass: 'bg-gradient-to-br from-orange-50 to-orange-100',
    borderClass: 'border-orange-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  leisure: {
    color: 'text-cyan-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(8,145,178,0.3)]',
    bgClass: 'bg-gradient-to-br from-cyan-50 to-cyan-100',
    borderClass: 'border-cyan-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  environment: {
    color: 'text-amber-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(217,119,6,0.3)]',
    bgClass: 'bg-gradient-to-br from-amber-50 to-amber-100',
    borderClass: 'border-amber-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
  family: {
    color: 'text-sky-600',
    shadow: 'drop-shadow-[0_1px_2px_rgba(79,70,229,0.3)]',
    bgClass: 'bg-gradient-to-br from-sky-50 to-sky-100',
    borderClass: 'border-sky-200',
    cardHoverBg: 'hover:bg-gray-50/80'
  },
};

/**
 * DEFAULT_VARIANT - 預設樣式（fallback）
 *
 * 當 category 未定義或無效時使用的預設樣式
 * 使用中性灰色系，保持視覺一致性
 */
export const DEFAULT_VARIANT: CategoryVariant = {
  color: 'text-gray-600',
  shadow: 'drop-shadow-[0_1px_1px_rgba(0,0,0,0.1)]',
  bgClass: 'bg-gradient-to-br from-gray-50 to-gray-100',
  borderClass: 'border-gray-200',
  cardHoverBg: 'hover:bg-gray-50/80'
};
